import type { UUID } from 'crypto'

import configuration from '../configuration'
import type {
  Article,
  GeneratedContent,
  MediaInfo,
  MediaPage,
  NotificationConfiguration,
  Licence,
  Customer,
} from '../types/domain'
import { GenerationStatus } from '../types/model'
import * as Storage from '../storage'
import { ApiPagination, ApiResponse, ApiTokenResponse } from '../types/api'
import { urlSearchformat } from '../format'
import authFetch from './authFetch'

const { PAYPER_BACKEND_BASE_ROUTE } = configuration as Record<string, string>

export const getMediaProviders = async (): Promise<MediaInfo[] | null> => {
  const response = await fetch(`${PAYPER_BACKEND_BASE_ROUTE}/media-providers`)

  if (response.ok) {
    return await response.json()
  } else {
    return null
  }
}

export const getMediaProviderById = async (
  id: UUID,
): Promise<Required<MediaInfo> | null> => {
  const response = await fetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/media-providers/${id}`,
  )

  if (response.ok) {
    return await response.json()
  } else {
    return null
  }
}

export interface IsUserAuthenticatedResponse {
  isAuthenticated: boolean
}

// TEMPORAL - remove some weeks after ending July 25
// This is acting as a new '/auth/verify' endpoint but it is using the path '/auth/refresh'
// in order to recover previous refresh-token cookie set exclusively for this particular path
export const verifyIsUserAuthenticated =
  async (): Promise<IsUserAuthenticatedResponse> => {
    const localStorageSession = Storage.getDeprecatedUserSession()
    const response = await authFetch(
      `${PAYPER_BACKEND_BASE_ROUTE}/auth/refresh`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: localStorageSession
          ? JSON.stringify(localStorageSession)
          : undefined,
      },
    )
    if (response.ok) {
      if (localStorageSession) {
        Storage.clearDeprecatedSession()
        window.location.reload()
      }

      return (await response.json()) as IsUserAuthenticatedResponse
    }
    return { isAuthenticated: false }
  }
// export const verifyIsUserAuthenticated =
//   async (): Promise<IsUserAuthenticatedResponse> => {
//     const response = await authFetch(`${PAYPER_BACKEND_BASE_ROUTE}/auth/verify`)
//     if (response.ok) {
//       return (await response.json()) as IsUserAuthenticatedResponse
//     }
//     return { isAuthenticated: false }
//   }

export const clearSession = async (): Promise<void> => {
  await fetch(`${PAYPER_BACKEND_BASE_ROUTE}/auth/clear-session`, {
    method: 'DELETE',
  })
}

export const getPageDataByUrl = async (
  url: string,
): Promise<MediaPage | null> => {
  const response = await fetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/articles/url?encodedUrl=${url}`,
  )

  if (response.ok) {
    return await response.json()
  } else {
    return null
  }
}

export const getUserOneTimeNotification = async (config: {
  isArticle: boolean
}): Promise<NotificationConfiguration | null> => {
  const configParams = new URLSearchParams({
    is_article: config.isArticle.toString(),
  })
  const response = await authFetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/user/notifications/one-time?${configParams.toString()}`,
    {
      method: 'POST',
    },
  )

  if (response.ok && response.status === 200) {
    return await response.json()
  } else {
    return null
  }
}

interface LicenceResponse {
  licence: {
    id: number // user-licence id
    licenceId: UUID // licence id
    isActive: boolean
    configuration: {
      popup: {
        brandAttribution: {
          enabled: boolean
        }
      }
    }
  }
  customer: Customer
}

export const getUserLicence = async (): Promise<Licence | null> => {
  const licenceResponse = await authFetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/user/licence`,
  )

  if (licenceResponse.ok) {
    const { licence, customer }: LicenceResponse = await licenceResponse.json()

    return {
      id: licence.licenceId,
      isActive: licence.isActive,
      configuration: licence.configuration,
      customer: { id: customer.id, name: customer.name },
    }
  }
  console.error('Error retrieving user licence')
  return null
}

export const getIsFavorited = async (
  mediaProviderId: UUID,
  articleId: string,
): Promise<boolean | null> => {
  const response = await authFetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/user/favorite-article/media/${mediaProviderId}/article/${articleId}`,
  )

  if (response.ok) {
    return response.json().then((r) => r.favorite)
  } else {
    console.error('Error retrieving if article is favorite')
    return null
  }
}

export const toggleIsFavorited = async (
  isFavorited: boolean,
  mediaProviderId: UUID,
  articleId: string,
): Promise<void> => {
  const response = await authFetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/user/favorite-article/media/${mediaProviderId}/article/${articleId}`,
    {
      method: isFavorited ? 'DELETE' : 'POST',
    },
  )
  if (!response.ok) {
    console.error('Error setting article as favorite')
  }
}

// PAYPER-1123: resumen/contexto se generan de forma asíncrona en el back, que
// distingue el estado por CÓDIGO HTTP, no por el body:
// - 200 -> listo. El body es el contenido en crudo (no { status, data }).
// - 202 -> en proceso. Body: { status: "PROCESSING" }.
// - 503 (o cualquier otro no-200/202) -> error. Body: { status: "ERROR" }.
// El campo `status` del body viene en mayúsculas y solo se usa como
// información adicional para logging: el estado real lo marca el código HTTP.
const parseGeneratedContentResponse = async (
  response: Response,
  errorMessage: string,
): Promise<GeneratedContent<string>> => {
  if (response.status === 200) {
    const data = await response.text()
    return { status: GenerationStatus.SUCCESS, data }
  }

  if (response.status === 202) {
    return { status: GenerationStatus.PROCESSING, data: null }
  }

  console.error(errorMessage)
  return { status: GenerationStatus.ERROR, data: null }
}

export const getArticleSummary = async (
  mediaProviderId: UUID,
  articleId: string,
): Promise<GeneratedContent<string>> => {
  const response = await authFetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/user/articles/media/${mediaProviderId}/article/${articleId}/summary`,
  )

  return parseGeneratedContentResponse(
    response,
    "Error retrieving article's summary",
  )
}

export const getArticleContext = async (
  mediaProviderId: UUID,
  articleId: string,
): Promise<GeneratedContent<string>> => {
  const response = await authFetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/user/articles/media/${mediaProviderId}/article/${articleId}/context`,
  )

  return parseGeneratedContentResponse(
    response,
    "Error retrieving article's context",
  )
}

const COVERAGE_DEFAULT_PAGINATION: ApiPagination = {
  offset: 0,
  limit: 12,
}

export const getArticleCoverage = async (
  mediaProviderId: UUID,
  articleId: string,
  medias?: UUID[],
): Promise<Article[] | null> => {
  let body
  if (medias) {
    body = JSON.stringify({ medias })
  }
  const response = await authFetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/user/articles/media/${mediaProviderId}/article/${articleId}/coverage?${urlSearchformat(
      COVERAGE_DEFAULT_PAGINATION,
    )}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    },
  )

  if (response.ok) {
    return (response.json() as Promise<ApiResponse<Article[]>>).then(
      (r) => r.data,
    )
  } else {
    console.error("Error retrieving article's coverage")
    return null
  }
}

const SEARCH_LIMIT = 50
interface SearchEndpointQueryParams extends Record<string, unknown> {
  value: string
  ['media-id']?: UUID
  limit: number
}
export const getArticlesSearchResults = async (
  searchValue: string,
  mediaId?: UUID,
): Promise<Article[] | null> => {
  const searchParams: SearchEndpointQueryParams = {
    value: searchValue,
    limit: SEARCH_LIMIT,
  }
  let body
  if (mediaId) {
    body = JSON.stringify({ medias: [mediaId] })
  }

  const response = await authFetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/articles/search?${urlSearchformat(
      searchParams,
    )}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    },
  )

  if (response.ok) {
    return (response.json() as Promise<ApiTokenResponse<Article[]>>).then(
      (r) => r.data,
    )
  } else {
    console.error('Error retrieving search results')
    return null
  }
}

/*
getMediabyCustomer 
18/01/2026: Obtener los medios configurados por un cliente
*/
export const getMediabyCustomerLicence = async (
  customerId: UUID,
  licenceId: UUID,
): Promise<MediaInfo[] | null> => {
  const response = await fetch(
    `${PAYPER_BACKEND_BASE_ROUTE}/media-providers/customer/${customerId}/licence/${licenceId}`,
  )

  if (response.ok) {
    return response.json()
  } else {
    console.error("Error retrieving media's by customer licence")
    return null
  }
}
